import { Typography, Paper, Divider, Box } from "@mui/material";
import Chart from "react-apexcharts";
import moment from "moment";

function SectionProgressChart({ sectionName, dataPoints }) {
  const validPoints = dataPoints.filter((p) => p.level != null);

  const categories = validPoints.map((p) => `#${p.assessment}`);
  const levels = validPoints.map((p) => Number(p.level));
  const maxLevel = levels.length > 0 ? Math.max(...levels) : 0;
  const yMax = Math.max(maxLevel, 4);

  const chartId = `assess-chart-${sectionName
    .replace(/[^a-zA-Z0-9]/g, "-")
    .toLowerCase()}`;

  const lineColor = "#1565c0";
  const fillTo = "#90caf9";

  const options = {
    chart: {
      id: chartId,
      toolbar: { show: false },
      animations: { enabled: false },
      fontFamily: "inherit",
    },
    legend: { show: false },
    stroke: {
      curve: "smooth",
      width: 3,
      lineCap: "round",
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.15,
        gradientToColors: [fillTo],
        inverseColors: false,
        opacityFrom: 0.35,
        opacityTo: 0.02,
        stops: [0, 100],
      },
    },
    markers: {
      size: 6,
      strokeWidth: 2,
      strokeColors: "#fff",
      hover: { sizeOffset: 2 },
      colors: [lineColor],
    },
    colors: [lineColor],
    xaxis: {
      categories,
      axisBorder: { show: true, color: "#cfd8dc" },
      axisTicks: { show: true, color: "#cfd8dc" },
      title: {
        text: "Assessment Number",
        offsetY: 6,
        style: { fontSize: "13px", fontWeight: 600, color: "#455a64" },
      },
      labels: {
        style: { fontSize: "11px", colors: "#546e7a" },
      },
    },
    yaxis: {
      min: 0,
      max: yMax,
      tickAmount: Math.min(yMax + 1, 11),
      title: {
        text: "Level",
        rotate: -90,
        style: { fontSize: "13px", fontWeight: 600, color: "#455a64" },
      },
      labels: {
        formatter: (v) => (Number.isInteger(v) ? String(v) : ""),
        style: { fontSize: "11px", colors: "#546e7a" },
        maxWidth: 40,
      },
    },
    tooltip: {
      theme: "light",
      custom: ({ dataPointIndex }) => {
        const point = validPoints[dataPointIndex];
        if (!point) {
          return "";
        }
        return `<div style="padding:8px 12px;font-size:13px;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,0.12);">
          <strong style="color:#1565c0;">Assessment ${point.assessment}</strong><br/>
          <span style="color:#455a64;">Level:</span> <strong>${point.level}</strong>
        </div>`;
      },
    },
    grid: {
      borderColor: "#eceff1",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 8, right: 20, bottom: 4, left: 56 },
    },
  };

  const series = [{ name: "Level", data: levels }];

  return (
    <Box>
      <Typography
        variant="subtitle1"
        component="h3"
        sx={{
          fontWeight: 700,
          color: "primary.dark",
          mb: 1.5,
          lineHeight: 1.35,
        }}
      >
        {sectionName}
      </Typography>
      <Box
        sx={{
          "& .apexcharts-yaxis text": { fill: "#546e7a" },
          "& .apexcharts-xaxis text": { fill: "#546e7a" },
          "& .apexcharts-yaxis-title text": { fill: "#455a64 !important" },
        }}
      >
        <Chart options={options} series={series} type="area" height={280} />
      </Box>
    </Box>
  );
}

function AssessmentReportBody({ reportData }) {
  const { firstName = "Patient", lastName = "", evaluationDate, sections = {} } =
    reportData;

  const sectionEntries = Object.entries(sections).filter(
    ([, dataPoints]) => Array.isArray(dataPoints) && dataPoints.length > 0
  );

  return (
    <Paper className="p-4" elevation={2}>
      <header className="text-center mb-4">
        <img
          src="../../assets/melillo_method_image.png"
          width={90}
          alt="Dr. Robert Melillo"
        />
        <Typography variant="h3" className="mt-2">
          Patient: <u>{firstName} {lastName}</u>
        </Typography>
        {evaluationDate && (
          <Typography variant="h3" className="mt-1">
            Evaluation Date:{" "}
            <u>{moment(evaluationDate).format("MM/DD/YYYY")}</u>
          </Typography>
        )}
      </header>

      <Divider className="mb-3" />

      <Typography variant="body2" className="mb-3" style={{ fontStyle: "italic", color: "#555" }}>
        This report shows the patient's level progression across assessment
        sessions for each evaluated section. Each chart plots the level recorded
        at successive assessments.
      </Typography>

      {sectionEntries.length === 0 ? (
        <Typography variant="body1" className="text-center" style={{ color: "#888" }}>
          No progress data available.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            alignItems: "stretch",
          }}
        >
          {sectionEntries.map(([sectionName, dataPoints], idx) => (
            <Paper
              key={`section-${idx}`}
              variant="outlined"
              sx={{
                flex: "1 1 calc(50% - 12px)",
                maxWidth: { xs: "100%", sm: "calc(50% - 12px)" },
                minWidth: 0,
                p: 2.5,
                borderRadius: 2,
                borderColor: "rgba(27, 54, 93, 0.18)",
                bgcolor: "#fff",
                boxShadow:
                  "0 1px 3px rgba(27,54,93,0.06), inset 0 1px 0 rgba(255,255,255,1)",
              }}
            >
              <SectionProgressChart
                sectionName={sectionName}
                dataPoints={dataPoints}
              />
            </Paper>
          ))}
        </Box>
      )}
    </Paper>
  );
}

const AssessmentFormReport = ({ reportData = {} }) => {
  return (
    <Paper elevation={0} className="py-2 px-2">
      <Box
        className="p-3"
        sx={{
          backgroundColor: "#e6f0ff",
          borderRadius: "5px",
        }}
      >
        <Typography variant="h2">Assessment Progress Report</Typography>
      </Box>

      <AssessmentReportBody reportData={reportData} />
    </Paper>
  );
};

export default AssessmentFormReport;
